// Centralised money formatting + currency selection for the billing UI. The
// gateway returns amounts as plain numbers in major units alongside an ISO
// currency code; we render the currencies we deploy with their symbol.

const CURRENCY_SYMBOL: Record<string, string> = {
  RUB: "₽",
  EUR: "€",
};

/**
 * Format a money amount for display.
 *
 * - Integers render without decimals; fractional amounts keep two.
 * - USD keeps the prefixed sign ("$ 99"); other currencies suffix the symbol in
 *   the local convention ("3900 ₽", "45 €"), falling back to the bare currency
 *   code when we have no symbol ("50 GBP").
 */
export function formatMoney(amount: number, currency: string): string {
  const value = Number.isFinite(amount) ? amount : 0;
  const trimmed = Number.isInteger(value) ? value.toString() : value.toFixed(2);
  const code = currency?.toUpperCase() ?? "";
  if (code === "USD") return `$ ${trimmed}`;
  const symbol = CURRENCY_SYMBOL[code] ?? code;
  return `${trimmed} ${symbol}`.trim();
}

/**
 * The currency we charge in for a given UI locale. Deployments are host-locked
 * (one host = one locale = one currency), so the client never needs a currency
 * switcher: RU → rubles, EN → euros.
 */
export function localeCurrency(lang: "en" | "ru"): string {
  return lang === "ru" ? "RUB" : "EUR";
}

/**
 * Pick the price to charge from a plan's / scan-pack's per-currency list: the
 * preferred (locale) currency if the backend provides it, else the first
 * available (fallback). Returns null when there are no prices. Generic over the
 * amount field name (`amount` for plans, `price` for scan-packs).
 */
export function pickPrice<T extends { currency: string }>(
  prices: T[] | null | undefined,
  preferredCurrency: string,
): T | null {
  if (!prices || prices.length === 0) return null;
  const want = preferredCurrency.toUpperCase();
  return prices.find((p) => p.currency.toUpperCase() === want) ?? prices[0];
}
